import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';

import { Observable } from 'rxjs';

import { SessionService } from '../session.service';

@Injectable({
    providedIn: 'root'
})
export class RedirectService {
    constructor(private router: Router, private sessionService: SessionService) { }

    /**
     * Guard que prefija el slug del centro guardado en cookie a la URL cuando se accede
     * desde el dominio master (easy.trainingym.com) sin slug en la URL.
     *
     * Solo actúa cuando NO se está accediendo desde un dominio CNAME personalizado
     * (isCustomDomain=false). En accesos por CNAME, el dominio ya identifica al centro,
     * por lo que no se necesita prefijar nada y `checkCenterInfo` resolverá el centro
     * leyendo `window.location.hostname`.
     *
     * Casuísticas que maneja (cuando NO es custom domain):
     *  - URL = `/` y hay cookie `domain`: redirige a `/<slug>`.
     *  - URL = `/<algo>` y `<algo>` no coincide con la cookie `domain`: redirige a `/<slug>/<algo>`.
     *  - Cookie `domain` vacía (primera visita): deja pasar la navegación para que
     *    `checkCenterInfo` resuelva el centro a partir del hostname. Sin este early
     *    return, una URL con path (ej. `/register`) entraría en bucle infinito porque
     *    `navigateByUrl('' + '/register')` re-dispararía el mismo guard indefinidamente.
     */
    checkAndRedirect(state: RouterStateSnapshot): Observable<boolean> {
        return new Observable(observable => {
            // Acceso por CNAME custom: el centro se identifica por hostname, no por slug.
            if (!this.sessionService.getIsCustomDomain()) {
                const splitsUrl = state.url.split('/');
                if (splitsUrl.length >= 2) {
                    const domain = this.sessionService.getDomain();
                    const nav = this.router.getCurrentNavigation();

                    // URL raíz: `/` → split = ['', ''] → splitsUrl[1] === ''.
                    if (splitsUrl[1] === '') {
                        // Sin slug en cookie no podemos redirigir; dejamos pasar para que
                        // `checkCenterInfo` resuelva el centro desde el hostname.
                        if (!domain) {
                            observable.next(true);

                            return;
                        }
                        // Redirige `/` → `/<slug>` preservando el state de navegación si existe.
                        if (nav && nav.extras.state) {
                            this.router.navigateByUrl(domain, { state: nav.extras.state });
                        } else {
                            this.router.navigateByUrl(domain);
                        }
                    } else if (domain !== splitsUrl[1]) {
                        // URL con path cuyo primer segmento no es el slug guardado.
                        // Sin slug en cookie no podemos prefijar nada; dejar pasar evita
                        // el bucle `navigateByUrl('' + state.url) === state.url`.
                        if (!domain) {
                            observable.next(true);

                            return;
                        }
                        // Prefija el slug: `/register` → `/<slug>/register`.
                        if (nav && nav.extras.state) {
                            this.router.navigateByUrl(domain + state.url, { state: nav.extras.state });
                        } else {
                            this.router.navigateByUrl(domain + state.url);
                        }
                    }
                }
            }

            observable.next(true);
        });
    }
}
